/**
 * Custom hook to track and calculate account resource usage and limits.
 * 
 * @param {string} accountId - The ID of the account to check limits for
 * @returns {Object} Account limits and usage information
 * @returns {Object} return.integrations - Integration limits data
 * @returns {number} return.integrations.used - Number of integrations currently in use
 * @returns {number|null} return.integrations.limit - Maximum allowed integrations (null if unlimited)
 * @returns {number|null} return.integrations.available - Number of integrations still available (null if unlimited)
 * @returns {number} return.integrations.percentage - Usage percentage (0-100)
 * @returns {boolean} return.integrations.isUnlimited - Whether this resource has unlimited usage
 * @returns {boolean} return.integrations.isFull - Whether the usage limit has been reached
 * @returns {string} return.integrations.name - Getting the display name of the integration limit
 * 
 * @returns {Object} return.nativeDashboards - Dashboard limits data (same structure as integrations)
 * @returns {Object} return.adminSeats - Admin user seat limits data (same structure as integrations)
 * @returns {Object} return.nonAdminSeats - Regular user seat limits data (same structure as integrations)
 * @returns {Object} return.privateViews - Private view limits data (same structure as integrations)
 * @returns {Object} return.sharedViews - Shared view limits data (same structure as integrations)
 * 
 * @returns {boolean} return.loading - Whether the data is still loading
 * @returns {boolean} return.error - Whether an error occurred while fetching data
 * @returns {string} return.message - Error message if an error occurred
 * 
 */
import { useAccountStore } from "../contexts/AccountContext";
import { useDashboardListStore } from "../contexts/DashboardListContext";
import { useUserStore } from "../contexts/UserContext";
import { useAccountDataTableStore } from "../contexts/AccountDataTableContext";
import { useRoleStore } from "../contexts/RoleContext";
import { useMemo } from "react";

export default function useAccountLimits(accountId) {
    const { accounts } = useAccountStore();
    const { dashboards } = useDashboardListStore();
    const { users } = useUserStore();
    const { accountdatatables } = useAccountDataTableStore();
    const { roles } = useRoleStore();

    return useMemo(() => {
        if (!accountId) return { error: true, loading: false, message: "Account ID is required" };
        if (accounts === undefined || accounts === null) return { error: false, loading: true };

        const account = accounts[accountId];
        if (!account) return { error: true, message: "Account not found" };

        const getLimit = (slug) => {
            const found = account.AccountLimits?.find((l) => l.Limit?.slug === slug);
            return found?.unlimited ? null : found?.amount ?? 0; // null represents unlimited
        };

        const getName = (slug) => {
            const found = account.AccountLimits?.find((l) => l.Limit?.slug === slug);
            return found?.Limit?.name ? found.Limit.name : slug; 
        };

        // Calculate used resources
        const usedDashboards = dashboards ?
            Object.values(dashboards).filter(
                (d) => d.AccountId === accountId && !d.embedUrl
            ).length : 0;

        const usedIntegrations = accountdatatables ? Object.keys(accountdatatables).length : 0;

        let adminSeatsUsed = 0;
        let nonAdminSeatsUsed = 0;

        if (users && roles) {
          Object.values(users).forEach((user) => {
            if (
              user.deleted ||
              user.pendingToDelete ||
              user.requestToJoin ||
              user.isSuperAdmin
            )
              return;

            const userRoles = Object.values(user.UserRoles || {});
            const userRole = userRoles.find((ur) => ur.AccountId === accountId);

            if (!userRole || !userRole.Role) return;

            const role = roles[userRole.Role.id];
            if (!role) return;

            if (role.adminSeat) {
              adminSeatsUsed++;
            } else {
              nonAdminSeatsUsed++;
            }
          });
        }

        // Format each limit with availability and percentage
        const formatLimitData = (used, limit) => {
            const isUnlimited = limit === null;
            const available = isUnlimited ? null : limit - used;
            const percentage = isUnlimited ? 0 : (limit > 0 ? (used / limit) * 100 : 100);

            return {
                used,
                limit,
                available,
                percentage,
                isUnlimited,
                isFull: !isUnlimited && available <= 0
            };
        };

        return {
            integrations: {...formatLimitData(usedIntegrations, getLimit("integrations")), name: getName("integrations")},
            nativeDashboards: {...formatLimitData(usedDashboards, getLimit("nativeDashboards")), name: getName("nativeDashboards")}, 
            adminSeats: {...formatLimitData(adminSeatsUsed, getLimit("adminSeats")), name: getName("adminSeats")},
            nonAdminSeats: {...formatLimitData(nonAdminSeatsUsed, getLimit("nonAdminSeats")), name: getName("nonAdminSeats")},
            privateViews: {...formatLimitData(0, getLimit("privateViews")), name: getName("privateViews")},
            sharedViews: {...formatLimitData(0, getLimit("sharedViews")), name: getName("sharedViews")},
            loading: false,
            error: false
        };
    }, [accountId, accounts, dashboards, users, accountdatatables, roles]);
}